import type { NotifyCategory, NotifyKind } from '@alphadesk/ontology/objects/notification.ts';
import type { WireKindTally } from '@alphadesk/web/contract.ts';
import {
  alarmVarOf,
  type CategoryCount,
  categoryCounts,
  type InboxLens,
  isLensed,
  NO_LENS,
  tallyTotals,
} from '../lib/notes.ts';
import { linkClass } from './primitives/controls.tsx';
import { type ChipEntry, ChipRow, Segmented } from './Toolbar.tsx';

const READ_FILTERS = ['everything', 'unread'] as const;
type ReadFilter = (typeof READ_FILTERS)[number];

export function InboxLensBar({
  lens,
  patch,
  tally,
}: {
  lens: InboxLens;
  patch: (change: Partial<InboxLens>) => void;
  tally: readonly WireKindTally[];
}) {
  const categories = categoryCounts(tally);
  const totals = tallyTotals(tally);
  const countOf = (of: { total: number; unread: number }) =>
    lens.unreadOnly ? of.unread : of.total;
  const open = categories.find((entry) => entry.category === lens.category) ?? null;

  if (categories.length === 0) return null;

  const categoryChips: ChipEntry[] = [
    { value: null, label: 'All news', count: countOf(totals) },
    ...categories.map((entry) => ({
      value: entry.category,
      label: entry.label,
      title: entry.blurb,
      count: countOf(entry),
    })),
  ];

  return (
    <section
      aria-label="Filter the inbox"
      className="-mt-1 flex flex-col gap-2.5 border-b border-line-soft pb-3"
    >
      <div className="flex items-center gap-3">
        <Segmented
          options={READ_FILTERS}
          value={lens.unreadOnly ? 'unread' : 'everything'}
          onChange={(next: ReadFilter) => patch({ unreadOnly: next === 'unread' })}
          label="which rows to show"
        />
        {isLensed(lens) && (
          <button
            type="button"
            onClick={() => patch(NO_LENS)}
            className={linkClass('faint', 'ml-auto shrink-0')}
          >
            Clear filters
          </button>
        )}
      </div>
      <div className="no-scrollbar flex overflow-x-auto sm:min-w-0">
        <ChipRow
          label="sort of news"
          selected={lens.category}
          onSelect={(picked) => patch({ category: picked as NotifyCategory | null, kind: null })}
          entries={categoryChips}
        />
      </div>
      {open !== null && open.kinds.length > 1 && (
        <KindChips
          key={open.category}
          category={open}
          selected={lens.kind}
          onSelect={(kind) => patch({ kind })}
          countOf={countOf}
        />
      )}
    </section>
  );
}

/** Keyed on the category by the caller so the reveal animation replays on switch. */
function KindChips({
  category,
  selected,
  onSelect,
  countOf,
}: {
  category: CategoryCount;
  selected: NotifyKind | null;
  onSelect: (kind: NotifyKind | null) => void;
  countOf: (of: { total: number; unread: number }) => number;
}) {
  return (
    <div className="ticket-in no-scrollbar ml-1 flex overflow-x-auto border-l border-line pl-3">
      <ChipRow
        label={`which ${category.label.toLowerCase()}`}
        selected={selected}
        onSelect={(picked) => onSelect(picked as NotifyKind | null)}
        entries={category.kinds.map((entry) => ({
          value: entry.kind,
          label: entry.label,
          count: countOf(entry),
          tint: alarmVarOf(entry.kind),
        }))}
      />
    </div>
  );
}
